import classNames from "classnames";
import { Loading } from "../elements/Loading";
import InvalidRole from "../shell/InvalidRole";
import EntityInformation from "./EntityInformation";
import { EntityPageProvider } from "./EntityPageProvider";
import { EntityPageContent } from "./EntityPageContent";
import { EntityWizard } from "./EntityWizard";
import { EntityPageFooter } from "./EntityPageFooter";
import { PageBar } from "./PageBar";
import { EntityPageNoView } from "./EntityPageNoView";
import { useEntityPage } from "./useEntityPage";

export interface EntityPageProps {
  entityName: string;
}

function EntityPageLayout() {
  const {
    entityName,
    definition,
    record,
    loading,
    error,
    notFound,
    isAllowed,
    hasView,
    informationVisible,
    wizard,
    formRef,
  } = useEntityPage();

  if (!isAllowed) {
    return <InvalidRole />;
  }

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center w-100">
        <Loading />
      </div>
    );
  }

  if (error || !record) {
    // A read that came back without a record is a missing record too, not a failure.
    const recordNotFound = notFound || !error;

    return (
      <div className="d-flex flex-grow-1 align-items-center justify-content-center p-4">
        <div
          className="alert alert-danger mb-0 text-center"
          role="alert"
        >
          <p className="mb-0">
            {recordNotFound
              ? "This record couldn't be loaded"
              : "Unable to load this page."}
          </p>
        </div>
      </div>
    );
  }

  if (!hasView) {
    return <EntityPageNoView />;
  }

  const Body = definition?.body;

  return (
    <div
      id="entity-page"
      className="bg-body d-flex flex-column flex-grow-1"
    >
      <PageBar />
      <form
        ref={formRef}
        // Suppresses only the browser's bubbles; the controls still decide validity.
        noValidate
        // Validity is checked before saving; the buttons sit outside this form.
        onSubmit={(event) => event.preventDefault()}
        className={classNames(
          "flex-grow-1 min-h-0",
          // A column, so the page's content can grow to fill the form's height.
          (Body || wizard) && !informationVisible ? "d-flex" : "d-flex flex-column overflow-y-scroll"
        )}
      >
        {informationVisible ? (
          <div className="p-3 p-md-4">
            <EntityInformation label={entityName} state={record} />
          </div>
        ) : wizard ? (
          <EntityWizard />
        ) : Body ? (
          <Body />
        ) : (
          <EntityPageContent />
        )}
      </form>
      {!informationVisible && <EntityPageFooter />}
    </div>
  );
}

export function EntityPage({ entityName }: EntityPageProps) {
  return (
    <EntityPageProvider entityName={entityName}>
      <EntityPageLayout />
    </EntityPageProvider>
  );
}

export default EntityPage;
